import {
  useBulkUpdatePurchasedGoods,
  useInvalidatePurchasedGoodsLists,
} from '@shared/api/hooks/purchased-goods/bulk';
import {
  useBulkUpdateSoldGoods,
  useInvalidateSoldGoodsLists,
} from '@shared/api/hooks/sold-goods/bulk';
import { OperationReportTypeEnum } from '@shared/api/types';
import { useToast } from '@shared/components/ui/use-toast';
import { useCallback } from 'react';

/** The type an item can be changed to. */
export type ItemType = 'food' | 'packaging' | 'other';

/** The page an item is currently on — used to hide the no-op target option. */
export type CurrentItemType = ItemType | 'uncertain';

export interface ChangeItemTypeOptions {
  /** Pin every item to this base food (food target only). */
  basicFoodUuid?: string;
  /** How a food reclassification is modelled (food target only). */
  foodModel?: 'basic-food' | 'recipe';
  /** Pin every item to this packaging type (packaging target only). */
  packagingTypeUuid?: string;
  /** Pack-weight override (food and packaging targets). */
  itemsPerPack?: number;
  /** Item weight override (food target only). */
  itemMassOrVolume?: number;
  /** Unit of the item weight override (food target only). */
  itemUnitUuid?: string;
}

const TARGET_TOAST_TITLE: Record<ItemType, string> = {
  food: 'Updating to Food & drink',
  packaging: 'Updating to Packaging',
  other: 'Updating to Other goods',
};

const TARGET_DESTINATION: Record<'food' | 'packaging', string> = {
  food: 'Food & drink',
  packaging: 'Packaging',
};

/**
 * Change the item type of a batch of purchased or sold goods to food,
 * packaging or Other. Works from any source (Uncertain, Other, Food,
 * Packaging) — the backend strips any prior modelling on re-type so nothing
 * double-counts.
 *
 * The backend flips ``item_type`` synchronously when the bulk job is enqueued,
 * so the items leave their source list immediately; food/packaging then re-run
 * Reggie's item-cleanup-v2 with a forced type, while "Other" is a re-type (for
 * purchased goods, also a zero-emission materialise; for sold goods, simply
 * un-modelled — sold excluded goods are not materialised). ``basicFoodUuid``
 * (food only) pins every selected item to one base food.
 *
 * ``reportType`` selects the purchased vs sold bulk endpoints. Both hook sets
 * are instantiated unconditionally (rules of hooks); only the matching trio is
 * used. Defaults to purchase so existing purchased call sites are unchanged.
 */
export function useChangeItemType(
  reportType: OperationReportTypeEnum = OperationReportTypeEnum.purchase
) {
  const { toast } = useToast();
  const isSales = reportType === OperationReportTypeEnum.sales;

  // Both hooks are instantiated unconditionally (rules of hooks); only the one
  // matching reportType is used. Change-type invalidates the lists an item can
  // move between so each surface reflects server truth on the next refetch.
  const invalidatePurchased = useInvalidatePurchasedGoodsLists();
  const invalidateSold = useInvalidateSoldGoodsLists();
  const purchasedBulk = useBulkUpdatePurchasedGoods({
    onSuccess: invalidatePurchased,
  });
  const soldBulk = useBulkUpdateSoldGoods({ onSuccess: invalidateSold });

  const bulkUpdate = isSales ? soldBulk : purchasedBulk;

  const changeType = useCallback(
    async (
      uuids: string[],
      target: ItemType,
      options?: ChangeItemTypeOptions
    ): Promise<boolean> => {
      try {
        await bulkUpdate.mutateAsync({
          data: {
            action: 'change-type',
            uuids,
            targetType: target,
            // basicFoodUuid / foodModel / item weight only apply when
            // reclassifying to food; packagingTypeUuid only to packaging;
            // itemsPerPack to both modelled targets.
            ...(target === 'food'
              ? {
                  basicFoodUuid: options?.basicFoodUuid,
                  foodModel: options?.foodModel,
                  itemMassOrVolume: options?.itemMassOrVolume,
                  itemUnitUuid: options?.itemUnitUuid,
                }
              : {}),
            ...(target === 'packaging'
              ? { packagingTypeUuid: options?.packagingTypeUuid }
              : {}),
            ...(target !== 'other'
              ? { itemsPerPack: options?.itemsPerPack }
              : {}),
          },
        });
        const count = uuids.length;
        const plural = count === 1 ? '' : 's';
        toast({
          title: TARGET_TOAST_TITLE[target],
          description:
            target === 'other'
              ? `${count} item${plural} set aside as Other goods — they won't count towards your footprint.`
              : `Calculating emissions for ${count} item${plural} — they'll appear in ${TARGET_DESTINATION[target]} shortly.`,
        });
        return true;
      } catch (error) {
        toast({
          title: "Couldn't update item type",
          description: 'Something went wrong. Please try again.',
          variant: 'destructive',
        });
        return false;
      }
    },
    [bulkUpdate, toast]
  );

  return {
    changeType,
    isLoading: bulkUpdate.isLoading,
  };
}
